import { keepPreviousData } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { PlusIcon, TruckIcon } from 'lucide-react';
import { useNavigate } from 'react-router';

import type { CarrierDTO, CreateCarrierDTO } from '@invenco/shipping-interface';

import { Button } from '@/components/core/button';
import { DialogTrigger } from '@/components/core/dialog';
import {
  DataTable,
  DataTableFilterMenu,
  DataTablePageContent,
  DataTablePagination,
  DataTableRefreshMenu,
  DataTableToolbar,
  useDataTable,
  useDataTableState,
  useRowNavigationCallback,
} from '@/components/data-table';
import { FormDialog } from '@/components/forms/form-dialog';
import { Header, HeaderActions, HeaderIcon, HeaderTitle } from '@/components/header';
import { Page } from '@/components/layout';
import { useGateways } from '@/gateways/GatewayProvider';
import { fetchAllPages, formatDateTime } from '@/shared/helpers';
import { useGatewayMutation, useQueryWithInput } from '@/shared/hooks/queries';

import { CreateCarrierDialogContent } from './create-carrier-dialog-content';

const columnHelper = createColumnHelper<CarrierDTO>();

const columns = [
  columnHelper.accessor('name', { header: 'Name' }),
  columnHelper.accessor('code', { header: 'Code' }),
  columnHelper.accessor('carrierServices', {
    header: 'Services',
    cell: ({ getValue }) => {
      const services = getValue();
      return services
        ?.map(({ code }) => code)
        .sort()
        .join(', ');
    },
  }),
  columnHelper.accessor('createdAt', {
    header: 'Created',
    cell: ({ getValue }) => formatDateTime(getValue()),
  }),
];

export function CarrierList() {
  const navigate = useNavigate();
  const { shippingGateway } = useGateways();
  const onClickRow = useRowNavigationCallback<CarrierDTO>('/shipping/carriers');

  const { state, filters, onPaginationChange, onSortingChange } = useDataTableState({ columns });

  const { data, isLoading, isFetching, isPlaceholderData, isError, refetch } = useQueryWithInput({
    parentKey: 'carriers',
    input: { search: filters.search },
    query: (input, { signal }) =>
      fetchAllPages(({ cursor, take }) =>
        shippingGateway.getCarriers(
          { q: input.search, cursor, take, include: { carrierServices: true } },
          { signal },
        ),
      ),
    placeholderData: keepPreviousData,
  });

  const table = useDataTable({
    data,
    columns,
    isLoading,
    isLoadingWithStaleData: isFetching && isPlaceholderData,
    hasError: isError,
    manualPagination: false,
    manualSorting: false,
    autoResetPageIndex: false,
    onPaginationChange,
    onSortingChange,
    state,
  });

  const { mutate: createCarrier } = useGatewayMutation({
    mutationFn: (payload: CreateCarrierDTO) => shippingGateway.createCarrier(payload),
    linkedQuery: ['carriers'],
    linkResponseToQuery: false,
    successMessage: 'Carrier created',
    onSuccess: (newCarrier) => {
      void navigate(`/shipping/carriers/${newCarrier.id}`);
    },
  });

  return (
    <Page>
      <Header>
        <HeaderIcon>
          <TruckIcon />
        </HeaderIcon>
        <HeaderTitle>Carriers</HeaderTitle>
        <HeaderActions>
          <FormDialog>
            <DialogTrigger asChild>
              <Button>
                <PlusIcon />
                Create
              </Button>
            </DialogTrigger>
            <CreateCarrierDialogContent onSubmit={createCarrier} />
          </FormDialog>
        </HeaderActions>
      </Header>
      <DataTablePageContent>
        <DataTableToolbar>
          <DataTableFilterMenu filters={filters} />
          <DataTableRefreshMenu onRefresh={refetch} />
        </DataTableToolbar>
        <DataTable table={table} onClickRow={onClickRow} />
        <DataTablePagination table={table} />
      </DataTablePageContent>
    </Page>
  );
}
